import { useId, type ReactNode } from 'react'
import { Icon, type IconName } from '@/app/icons'
import { Button, cn } from './primitives'

/**
 * The page kit: the few shapes every module screen is built from.
 *
 * A page is a stack of panels. A list panel is a header or toolbar, a table,
 * and a footer. Keeping to these few shapes is what makes a new screen look
 * like it belongs without anyone designing it -- see docs/konvensi-ui.md for
 * when to use which.
 */

/** A white panel on the grey page. The only container a page should need. */
export function Panel({
  className,
  children,
  as: Tag = 'section',
  'aria-label': ariaLabel,
}: {
  className?: string
  children: ReactNode
  as?: 'section' | 'div'
  'aria-label'?: string
}) {
  return (
    <Tag aria-label={ariaLabel} className={cn('min-w-0 rounded-panel border border-line bg-card shadow-panel', className)}>
      {children}
    </Tag>
  )
}

/** Title row of a panel: name, optional count pill, one line of context, actions on the right. */
export function PanelHeader({
  title,
  count,
  countTone = 'primary',
  description,
  actions,
  as: Heading = 'h2',
}: {
  title: ReactNode
  /** h3 when the panel sits under another h2, e.g. sections inside a drawer. */
  as?: 'h2' | 'h3'
  count?: number
  countTone?: 'primary' | 'danger'
  description?: ReactNode
  actions?: ReactNode
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 border-b border-line px-5 py-4">
      <Heading className="text-base font-bold tracking-tight text-gray-900">{title}</Heading>
      {count !== undefined && count > 0 && (
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-xs font-bold tabular-nums',
            countTone === 'danger' ? 'bg-danger-light text-danger' : 'bg-primary-light text-primary',
          )}
        >
          {count}
        </span>
      )}
      {description && <span className="text-xs text-gray-500">{description}</span>}
      {actions && <div className="ml-auto flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  )
}

/** Inner padding for a panel's body, when it holds content rather than a table. */
export function PanelBody({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('px-5 py-5', className)}>{children}</div>
}

/**
 * Nothing to show. `good` when empty is the desired state (an inbox with
 * nothing waiting), `neutral` when it is simply empty (no documents yet).
 */
export function EmptyState({
  title,
  text,
  tone = 'neutral',
  icon,
  action,
}: {
  title: string
  text?: ReactNode
  tone?: 'good' | 'neutral'
  icon?: IconName
  action?: ReactNode
}) {
  return (
    <div className="flex flex-wrap items-center gap-4 px-5 py-8">
      <span
        className={cn(
          'grid size-12 shrink-0 place-items-center rounded-xl',
          tone === 'good' ? 'bg-success-light text-success' : 'bg-gray-200 text-gray-500',
        )}
      >
        <Icon name={icon ?? (tone === 'good' ? 'check' : 'document')} className="size-5" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-gray-900">{title}</p>
        {text && <p className="mt-0.5 text-gray-500">{text}</p>}
      </div>
      {action}
    </div>
  )
}

/** The strip above a table: filters on the left, search and extras on the right. */
export function Toolbar({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-center gap-x-3 gap-y-2.5 border-b border-line px-4 py-3">{children}</div>
}

export interface FilterOption<T extends string> {
  value: T
  label: string
  /** Omit when unknown: a 0 you are not sure of is worse than no number. */
  count?: number
}

/**
 * Mutually exclusive filters over one list, as a row of chips. The chosen one
 * is filled; counts sit inside the chip so the choice and its size read
 * together. For switching between different screens, use the menu instead.
 */
export function FilterChips<T extends string>({
  value,
  onChange,
  options,
  label,
}: {
  value: T
  onChange: (value: T) => void
  options: ReadonlyArray<FilterOption<T>>
  /** Names the group for screen readers, e.g. "Saring menurut status". */
  label: string
}) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-1.5">
      {options.map((option) => {
        const active = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(option.value)}
            className={cn(
              'inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-[0.8125rem] font-medium whitespace-nowrap transition-colors',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
              active ? 'border-gray-900 bg-gray-900 text-card' : 'border-line bg-card text-gray-600 hover:text-gray-900',
            )}
          >
            {option.label}
            {option.count !== undefined && (
              <span className={cn('tabular-nums', active ? 'text-white/70' : 'text-gray-400')}>{option.count}</span>
            )}
          </button>
        )
      })}
    </div>
  )
}

/** A search box for a toolbar. Pushes itself to the right end of the row. */
export function SearchField({
  value,
  onChange,
  placeholder,
  label,
  className,
}: {
  value: string
  onChange: (value: string) => void
  placeholder: string
  label: string
  className?: string
}) {
  const id = useId()
  return (
    <label
      htmlFor={id}
      className={cn(
        'ml-auto flex h-9 w-full items-center gap-2 rounded-md border border-line bg-gray-100 px-2.5 text-gray-500 sm:w-72',
        'focus-within:border-primary focus-within:bg-card',
        className,
      )}
    >
      <Icon name="search" className="size-4 shrink-0" />
      <span className="sr-only">{label}</span>
      <input
        id={id}
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
      />
    </label>
  )
}

/**
 * Table classes. A table is plain markup styled with these, inside
 * <TableScroll> -- wide enough for its columns, scrolling sideways on its own
 * so the page never does.
 */
export const table = {
  base: 'w-full border-collapse text-left',
  th: 'border-b border-line bg-gray-100/70 px-4 py-2.5 text-[0.7rem] font-semibold tracking-[0.06em] whitespace-nowrap text-gray-500 uppercase',
  td: 'border-b border-line px-4 py-3 align-middle',
  row: 'transition-colors last:[&>td]:border-b-0 hover:bg-gray-100/80',
  num: 'text-right tabular-nums whitespace-nowrap',
  /** The row's main identifier: document number, code. */
  key: 'font-mono font-semibold whitespace-nowrap text-gray-900',
  /** A secondary line under a cell's main text. */
  sub: 'block max-w-72 truncate text-xs text-gray-500',
  dim: 'text-xs whitespace-nowrap text-gray-500',
}

export function TableScroll({ minWidth = '52rem', children }: { minWidth?: string; children: ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <div style={{ minWidth }}>{children}</div>
    </div>
  )
}

/** The panel's last row: how much is shown, and paging when there is more. */
export function PanelFooter({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-line px-4 py-3 text-[0.8125rem] text-gray-500">
      {children}
    </div>
  )
}

export function Pagination({
  page,
  totalPages,
  first,
  last,
  onPage,
}: {
  /** Zero-based, as the API sends it. */
  page: number
  totalPages: number
  first: boolean
  last: boolean
  onPage: (page: number) => void
}) {
  if (totalPages <= 1) return null
  return (
    <div className="flex items-center gap-2">
      <span className="tabular-nums">
        Halaman {page + 1} dari {totalPages}
      </span>
      <Button type="button" variant="light" size="sm" disabled={first} onClick={() => onPage(Math.max(0, page - 1))} aria-label="Halaman sebelumnya">
        <Icon name="arrowLeft" className="size-4" />
      </Button>
      <Button type="button" variant="light" size="sm" disabled={last} onClick={() => onPage(page + 1)} aria-label="Halaman berikutnya">
        <Icon name="arrowRight" className="size-4" />
      </Button>
    </div>
  )
}
